import React, { useCallback, useState } from 'react';
import DatePicker from 'react-datepicker';
import ConfirmationMessage from '../../../Controls/ConfirmationMessage';

import { MAINTENANCE_FIELDS } from '../../common/fields';
import * as actions from '../../containers/Advanced/advancedActions';

import SelectField from '../Controls/SelectField/SelectField';
import SwitchField from '../Controls/SwitchField/SwitchField';
import TextInput from '../Controls/TextInput/TextInput';
import Label from '../Controls/Label/Label';

const OFFLINE_MODE_CONFIRMATION = {
    body2Offline: 'You are switching to Offline Mode. Are you sure?',
    body2Online: 'Switching to the online mode, the Admin Console will connect to the Mobile Messaging Server.',
    approveText: 'OK'
};

const Maintenance = ({ fields, dispatch, settings }) => {
    const nodeLogLevel = fields[MAINTENANCE_FIELDS.NODE_LOG_LEVEL.ID];
    const logLevel = fields[MAINTENANCE_FIELDS.LOG_LEVEL.ID];
    const pushServicesLogLevel = fields[MAINTENANCE_FIELDS.PUSH_SERVICES_LOG_LEVEL.ID];

    const offlineMode = fields[MAINTENANCE_FIELDS.OFFLINE_MODE.ID];
    const sendDiagnostics = fields[MAINTENANCE_FIELDS.SEND_DIAGNOSTICS.ID];
    const sendDiagnosticsAnon = fields[MAINTENANCE_FIELDS.SEND_DIAGNOSTICS_ANON.ID];
    const autoADAdapterUpgrade = fields[MAINTENANCE_FIELDS.AUTO_AD_ADAPTER_UPGRADE.ID];
    const allowAzureOpenAiUsage = fields[MAINTENANCE_FIELDS.ALLOW_AZURE_OPENAI_USAGE.ID];
    const addPasswordToLogs = fields[MAINTENANCE_FIELDS.ADD_PASSWORD_TO_LOGS.ID];
    const clientLogPassword = fields[MAINTENANCE_FIELDS.CLIENT_LOG_PASSWORD.ID];
    const allowSendLogsToSilverhub = fields[MAINTENANCE_FIELDS.ALLOW_SEND_LOGS_TO_SILVERHUB.ID];
    const expireSendLogsToSilverhubOn = fields[MAINTENANCE_FIELDS.EXPIRE_SEND_LOGS_TO_SILVERHUB.ID];

    const sendDiagnosticsFound = fields[MAINTENANCE_FIELDS.SEND_DIAGNOSTICS.ID];
    const sendDiagnosticsAnonFound = fields[MAINTENANCE_FIELDS.SEND_DIAGNOSTICS_ANON.ID];

    const showSendDiagnostics = sendDiagnosticsFound || sendDiagnosticsAnonFound;

    const showExpireSendLogsField =
        allowSendLogsToSilverhub && allowSendLogsToSilverhub.value && expireSendLogsToSilverhubOn;
    const [offlineConfirmationState, setOfflineConfirmationState] = useState({
        modalShown: false,
        value: null
    });

    const dispatchUpdate = useCallback(
        (fieldId, value) => {
            dispatch({
                type: actions.UPDATE_MAINTENANCE,
                payload: { fieldId, value }
            });
        },
        [dispatch]
    );

    const dispatchSendDiagnosticsUpdate = useCallback(
        (fieldId, value) => {
            if (value === false && sendDiagnosticsAnon.value) {
                dispatchUpdate(sendDiagnosticsAnon.id, false);
            }
            dispatchUpdate(fieldId, value);
        },
        [dispatchUpdate, sendDiagnosticsAnon]
    );

    const onOfflineModeChangedAttempt = useCallback((fieldId, value) => {
        setOfflineConfirmationState({ modalShown: true, value: value });
    }, []);

    return (
        <>
            {nodeLogLevel && (
                <SelectField field={nodeLogLevel} changed={dispatchUpdate} options={nodeLogLevel.options} />
            )}
            {logLevel && <SelectField field={logLevel} changed={dispatchUpdate} options={logLevel.options} />}
            {pushServicesLogLevel && (
                <SelectField
                    field={pushServicesLogLevel}
                    changed={dispatchUpdate}
                    options={pushServicesLogLevel.options}
                />
            )}
            {offlineMode && <SwitchField field={offlineMode} marginTop="large" changed={onOfflineModeChangedAttempt} />}
            {showSendDiagnostics && (
                <SwitchField field={sendDiagnostics} marginTop="large" changed={dispatchSendDiagnosticsUpdate}>
                    <SwitchField field={sendDiagnosticsAnon} marginTop="none" changed={dispatchUpdate} />
                </SwitchField>
            )}
            {autoADAdapterUpgrade && (
                <SwitchField field={autoADAdapterUpgrade} marginTop="large" changed={dispatchUpdate} />
            )}
            {allowAzureOpenAiUsage && (
                <SwitchField field={allowAzureOpenAiUsage} marginTop="large" changed={dispatchUpdate} />
            )}
            {allowSendLogsToSilverhub && (
                <>
                    <SwitchField field={allowSendLogsToSilverhub} marginTop="large" changed={dispatchUpdate} />
                    {showExpireSendLogsField && (
                        <div className="flex">
                            <Label value={expireSendLogsToSilverhubOn.name} bold={false} />
                            <DatePicker
                                placeholderText="Turned off manually"
                                isClearable={true}
                                className="w-[250px] h-[30px] inline-block text-sm border rounded mx-0 my-1 px-2 py-1 border-solid border-[#ccc] ml-2"
                                id={expireSendLogsToSilverhubOn.id}
                                onChange={(date) =>
                                    dispatchUpdate(expireSendLogsToSilverhubOn.id, date?.getTime() || null)
                                }
                                selected={
                                    expireSendLogsToSilverhubOn.value
                                        ? new Date(expireSendLogsToSilverhubOn.value)
                                        : null
                                }
                                value={new Date(expireSendLogsToSilverhubOn.value)}
                                minDate={new Date()}
                                nextMonthButtonLabel
                                previousMonthButtonLabel
                            />
                        </div>
                    )}
                </>
            )}
            {settings.allowViewEncryptedLogs && (
                <>
                    {addPasswordToLogs && (
                        <SwitchField field={addPasswordToLogs} marginTop="large" changed={dispatchUpdate} />
                    )}
                    {clientLogPassword && addPasswordToLogs.value && (
                        <TextInput
                            type={'password'}
                            field={clientLogPassword}
                            changed={dispatchUpdate}
                            placeholder="Enter password"
                        />
                    )}
                </>
            )}

            {offlineConfirmationState.modalShown && (
                <ConfirmationMessage
                    title={`Switch to ${offlineConfirmationState.value ? 'offline' : 'online'} mode`}
                    body={
                        offlineConfirmationState.value
                            ? OFFLINE_MODE_CONFIRMATION.body2Offline
                            : OFFLINE_MODE_CONFIRMATION.body2Online
                    }
                    approveText={OFFLINE_MODE_CONFIRMATION.approveText}
                    OnCancel={() =>
                        setOfflineConfirmationState({
                            modalShown: false,
                            value: null
                        })
                    }
                    OnApprove={() => {
                        dispatchUpdate(offlineMode.id, offlineConfirmationState.value);
                        setOfflineConfirmationState({
                            modalShown: false,
                            value: null
                        });
                    }}
                />
            )}
        </>
    );
};

export default React.memo(Maintenance);
